import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Calendar,
  ArrowRight,
  RefreshCw,
  Zap,
  Lock,
  ExternalLink,
  ChevronRight,
  Check,
  AlertCircle,
  Sparkles,
  Award,
  Wallet,
  Building2,
  Send,
  TrendingUp,
  Receipt,
  Download,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const ProfessionalSubscriptionPage: React.FC = () => {
  const { currentUser, navigateTo, viewParams } = useApp();

  const [loading, setLoading] = useState<boolean>(false);
  const [earnings, setEarnings] = useState<{
    totalGrossNaira: number;
    totalCommissionNaira: number;
    totalNetEarningsNaira: number;
    availableBalanceNaira: number;
    pendingEscrowNaira: number;
    settledPayoutsNaira: number;
    transactionsCount: number;
    transactions: any[];
    payouts: any[];
  }>({
    totalGrossNaira: 0,
    totalCommissionNaira: 0,
    totalNetEarningsNaira: 0,
    availableBalanceNaira: 0,
    pendingEscrowNaira: 0,
    settledPayoutsNaira: 0,
    transactionsCount: 0,
    transactions: [],
    payouts: [],
  });

  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState<string>('');
  const [bankName, setBankName] = useState<string>('Access Bank');
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [accountName, setAccountName] = useState<string>(currentUser?.fullName || '');
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState<string | null>(null);
  const [payoutError, setPayoutError] = useState<string | null>(null);

  const loadFinancials = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/marketplace/professionals/${currentUser.id}/earnings`);
      const data = await res.json();
      if (data.success && data.earnings) {
        const raw = data.earnings;
        setEarnings({
          totalGrossNaira: raw.totalGrossJobValue ?? raw.totalGrossNaira ?? 0,
          totalCommissionNaira: raw.totalJobMatchCommission ?? raw.totalCommissionNaira ?? 0,
          totalNetEarningsNaira: raw.totalProfessionalEarnings ?? raw.totalNetEarningsNaira ?? 0,
          availableBalanceNaira: raw.availableEarnings ?? raw.availableBalanceNaira ?? 0,
          pendingEscrowNaira: raw.pendingEarnings ?? raw.pendingEscrowNaira ?? 0,
          settledPayoutsNaira: raw.paidOutEarnings ?? raw.settledPayoutsNaira ?? 0,
          transactionsCount: raw.successfulTransactionsCount ?? raw.transactionsCount ?? 0,
          transactions: raw.recentTransactions ?? raw.transactions ?? [],
          payouts: raw.payoutHistory ?? raw.payouts ?? [],
        });
      }
    } catch (err) {
      console.warn('Error loading professional earnings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFinancials();
  }, [currentUser?.id]);

  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.id) return;

    const amt = Number(payoutAmount);
    if (!amt || amt <= 0) {
      setPayoutError('Please enter a valid payout amount.');
      return;
    }

    if (amt > (earnings.availableBalanceNaira || 0)) {
      setPayoutError(`Amount exceeds your available balance of ₦${(earnings.availableBalanceNaira || 0).toLocaleString()}`);
      return;
    }

    if (!accountNumber || accountNumber.length < 10) {
      setPayoutError('Please enter a valid 10-digit Nigerian NUBAN account number.');
      return;
    }

    setPayoutSubmitting(true);
    setPayoutError(null);
    setPayoutSuccess(null);

    try {
      const res = await fetch(`/api/marketplace/professionals/${currentUser.id}/payout-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amt,
          bankName,
          accountNumber,
          accountName,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setPayoutSuccess(data.message || 'Payout request submitted successfully!');
        setPayoutAmount('');
        await loadFinancials();
        setTimeout(() => {
          setIsPayoutModalOpen(false);
          setPayoutSuccess(null);
        }, 2000);
      } else {
        setPayoutError(data.error || 'Failed to submit payout request.');
      }
    } catch (err: any) {
      setPayoutError(err.message || 'Network error while requesting payout.');
    } finally {
      setPayoutSubmitting(false);
    }
  };

  const sampleBanks = [
    'Access Bank',
    'Zenith Bank',
    'Guaranty Trust Bank (GTBank)',
    'First Bank of Nigeria',
    'United Bank for Africa (UBA)',
    'Kuda Bank',
    'Opay / Paycom',
    'Palmpay',
    'Moniepoint MFB',
    'Stanbic IBTC Bank',
    'Sterling Bank',
    'Fidelity Bank',
    'Wema Bank / ALAT',
    'Union Bank of Nigeria',
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 animate-fade-in">
      {/* Top Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigateTo('pro_dashboard')}
              className="text-xs text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 font-semibold cursor-pointer"
            >
              ← Back to Professional Dashboard
            </button>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
            <Wallet className="w-7 h-7 text-emerald-600" />
            Financials, Commission & Payouts Center
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Zero subscription fees. Transparent 10% platform commission on completed customer jobs with Paystack escrow protection.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadFinancials}
            disabled={loading}
            className="px-3.5 py-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-xs font-bold text-slate-700 flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh Financials
          </button>

          <button
            onClick={() => setIsPayoutModalOpen(true)}
            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
          >
            <Send className="w-3.5 h-3.5" />
            Request Bank Payout
          </button>
        </div>
      </div>

      {/* Model Overview Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950 rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden shadow-xl border border-slate-800">
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Commission Marketplace Model • Zero Monthly Fees</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black text-white">
            You Keep 90% of All Job Billings
          </h2>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            JobMatch NG has removed all monthly subscription fees for verified Nigerian artisans. You can create your profile, receive local job leads, and submit quotes completely free. When a customer accepts and pays for a job, JobMatch NG charges a simple 10% platform commission.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Subscription Cost</span>
              <span className="text-lg font-black text-emerald-400 font-mono">₦0 / Month</span>
              <span className="text-[11px] text-slate-300 block mt-0.5">Free registration & quoting</span>
            </div>

            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Platform Commission</span>
              <span className="text-lg font-black text-white font-mono">10%</span>
              <span className="text-[11px] text-slate-300 block mt-0.5">Only upon customer payment</span>
            </div>

            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">Your Take-Home Payout</span>
              <span className="text-lg font-black text-emerald-400 font-mono">90% Net</span>
              <span className="text-[11px] text-slate-300 block mt-0.5">Direct to Nigerian bank</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Available Balance */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Available for Payout</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              ₦
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono mt-2">
            ₦{(earnings.availableBalanceNaira || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>Ready for bank disbursement</span>
          </div>
        </div>

        {/* Pending Escrow */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">In Escrow (Active Jobs)</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Lock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-blue-600 font-mono mt-2">
            ₦{(earnings.pendingEscrowNaira || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Released when customer confirms job
          </div>
        </div>

        {/* Gross Paid */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Gross Invoiced</span>
            <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono mt-2">
            ₦{(earnings.totalGrossNaira || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Across {earnings.transactionsCount || 0} customer payments
          </div>
        </div>

        {/* Total Settled Payouts */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Disbursed to Bank</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono mt-2">
            ₦{(earnings.settledPayoutsNaira || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Successfully paid out
          </div>
        </div>
      </div>

      {/* Commission Example & How Payouts Work */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-4">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            <span>How Paystack Escrow & Commission Works</span>
          </h3>

          <div className="space-y-3 text-xs text-slate-600 leading-relaxed">
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <span className="font-bold text-slate-900 block">Example Job: ₦100,000 agreed quote</span>
              <div className="flex justify-between border-b border-slate-200 pb-1">
                <span>Customer pays via Paystack:</span>
                <strong className="text-slate-900 font-mono">₦100,000 (100%)</strong>
              </div>
              <div className="flex justify-between border-b border-slate-200 pb-1 text-slate-500">
                <span>JobMatch NG platform fee:</span>
                <strong className="text-slate-700 font-mono">- ₦10,000 (10%)</strong>
              </div>
              <div className="flex justify-between pt-1 text-emerald-700 font-bold">
                <span>Artisan net earnings:</span>
                <strong className="text-emerald-700 font-mono text-sm">₦90,000 (90%)</strong>
              </div>
            </div>

            <p>
              1. Customer deposits money safely into escrow before you travel to the job site.
            </p>
            <p>
              2. Complete the physical job to the customer’s satisfaction.
            </p>
            <p>
              3. Upon customer confirmation or job mark-as-complete, your 90% net earnings transfer directly into your available balance.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs space-y-4">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span>Direct Nigerian Bank Payouts</span>
          </h3>

          <div className="space-y-3 text-xs text-slate-600 leading-relaxed">
            <p>
              Payouts are processed directly via NIP (Nigeria Interbank Settlement System) to any licensed commercial bank or CBN-approved fintech (Access, GTBank, Zenith, FirstBank, Kuda, Opay, Palmpay, Moniepoint).
            </p>

            <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200 space-y-2">
              <div className="flex items-center gap-2 text-emerald-900 font-bold">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Zero Withdrawal Fees</span>
              </div>
              <p className="text-[11px] text-emerald-800">
                You receive the full 90% net earnings into your bank account with zero platform withdrawal deductions.
              </p>
            </div>

            <button
              onClick={() => setIsPayoutModalOpen(true)}
              className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" />
              <span>Request Payout to Bank Account</span>
            </button>
          </div>
        </div>
      </div>

      {/* Transaction & Escrow Ledger Table */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">Job Payments & Escrow Ledger</h3>
            <p className="text-xs text-slate-500 mt-0.5">Itemized breakdown of customer payments and commission splits</p>
          </div>
          <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 py-1 rounded-lg">
            {earnings.transactions?.length || 0} Transactions
          </span>
        </div>

        {earnings.transactions && earnings.transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Job / Description</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4 text-right">Gross Amount</th>
                  <th className="py-3 px-4 text-right">JobMatch (10%)</th>
                  <th className="py-3 px-4 text-right">Net Payout (90%)</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4">Paystack Ref</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {earnings.transactions.map((tx: any) => (
                  <tr key={tx.id} className="hover:bg-slate-50/80">
                    <td className="py-3 px-4 text-slate-500 font-mono text-[11px]">
                      {new Date(tx.created_at || tx.createdAt).toLocaleDateString('en-NG')}
                    </td>
                    <td className="py-3 px-4 font-medium text-slate-900">
                      {tx.job_title || tx.jobTitle || 'Verified Trade Service'}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {tx.customer_name || tx.customerName || 'Customer'}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-slate-900">
                      ₦{(tx.gross_amount || tx.grossAmount || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-slate-500">
                      -₦{(tx.commission_amount || tx.commissionAmount || (tx.gross_amount * 0.1) || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                      ₦{(tx.professional_amount || tx.professionalAmount || (tx.gross_amount * 0.9) || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800">
                        {tx.payment_status || tx.paymentStatus || 'Verified'}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-[11px] text-slate-400 truncate max-w-[120px]">
                      {tx.payment_reference || tx.paymentReference || 'PAYSTACK-REF'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-10 text-center space-y-2">
            <Receipt className="w-8 h-8 text-slate-300 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">No Job Transactions Yet</h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              When clients accept your quotes and pay via Paystack, the full escrow breakdown and net payouts will appear here.
            </p>
            <button
              onClick={() => navigateTo('available_jobs')}
              className="mt-3 px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors cursor-pointer"
            >
              Browse Open Jobs
            </button>
          </div>
        )}
      </div>

      {/* Payout Request Modal */}
      {isPayoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                  <Send className="w-4 h-4" />
                </div>
                <h3 className="text-base font-extrabold text-slate-900">Request Bank Payout</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPayoutModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold"
              >
                ✕
              </button>
            </div>

            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
              <span className="text-xs text-emerald-800 block">Available Balance</span>
              <span className="text-2xl font-black text-emerald-950 font-mono">
                ₦{(earnings.availableBalanceNaira || 0).toLocaleString()}
              </span>
            </div>

            {payoutSuccess && (
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-900 text-xs font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{payoutSuccess}</span>
              </div>
            )}

            {payoutError && (
              <div className="p-3 rounded-xl bg-rose-100 text-rose-900 text-xs font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{payoutError}</span>
              </div>
            )}

            <form onSubmit={handleRequestPayout} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Payout Amount (₦)
                </label>
                <input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder="e.g. 50000"
                  min="1000"
                  max={earnings.availableBalanceNaira || 1000000}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm font-mono text-slate-900 focus:outline-none focus:border-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Select Nigerian Bank
                </label>
                <select
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:outline-none focus:border-emerald-500"
                >
                  {sampleBanks.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  10-Digit NUBAN Account Number
                </label>
                <input
                  type="text"
                  maxLength={10}
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="0123456789"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm font-mono text-slate-900 focus:outline-none focus:border-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Account Name (as registered on bank)
                </label>
                <input
                  type="text"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="Full Legal Name"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs text-slate-900 focus:outline-none focus:border-emerald-500"
                  required
                />
              </div>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsPayoutModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={payoutSubmitting}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {payoutSubmitting ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Submitting Payout...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Submit Payout Request</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
